import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, signal, WritableSignal } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { PaymentStandingBannerComponent, PaymentStandingStateService, ToastComponent } from "@zaeper/angular-dashboard-view-lib";
import { ILanguageStateService } from "@zaeper/localization-lib";
import { toSignal } from "@angular/core/rxjs-interop";
import { billingRestClientInjectionToken } from "../../rest-clients/billing/billing-rest-client-injection-token";
import { IBillingRestClient } from "../../rest-clients/billing/i-billing-rest.client";
import { languageStateServiceInjectionToken } from "../../services/language/languageStateServiceInjectionToken";
import { roleAtLeast$ } from "../../global-states/session/role-at-least";
import { ICompany } from "../../models/i-company";
import { ITwoFactorRestClient, MfaReminderBannerComponent, twoFactorRestClientInjectionToken } from "@zaeper/angular-2fa-lib";
import { DashboardLayoutComponent } from "../../layout/dashboard-layout/dashboard-layout.component";
import { WavesBackgroundComponent } from "../../components/waves-background/waves-background.component";
import { filter, map, Observable, Subscription } from "rxjs";
import { IStoreStateService, storeStateServiceInjectionToken } from "@zaeper/angular-application-store-lib";
import { sessionStateServiceInjectionToken } from "../../global-states/session/session-state-service-injection-token";
import { ISessionStateService } from "../../global-states/session/i-session-state.service";
import { IReceiver } from "../../models/i-receiver";
import { ICloud } from "../../models/i-cloud";
import { CommonModule, AsyncPipe } from "@angular/common";
import {ToastModule} from "primeng/toast";

@Component({
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    RouterOutlet,
    DashboardLayoutComponent,
    ToastComponent,
    WavesBackgroundComponent,
    MfaReminderBannerComponent,
    PaymentStandingBannerComponent
  ],
  templateUrl: "./app-outlet.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppOutletComponent implements OnInit, OnDestroy {
  public readonly routerPath$: Observable<string>;
  public readonly mfaEnabled: WritableSignal<boolean> = signal( true ); // start hidden until status loads
  /** The company's payment reminder; every page shows it while the standing isn't OK. */
  public readonly standing;
  public readonly isAdmin;
  public readonly language;
  public readonly openingPortal: WritableSignal<boolean> = signal( false );
  private _companyId: string | null = null;

  private readonly _subscriptions: Subscription = new Subscription();

  constructor(
    @Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
    @Inject(storeStateServiceInjectionToken) private readonly _storeStateService: IStoreStateService,
    @Inject(twoFactorRestClientInjectionToken) private readonly _twoFactorRestClient: ITwoFactorRestClient,
    @Inject(billingRestClientInjectionToken) private readonly _billingRestClient: IBillingRestClient,
    @Inject(languageStateServiceInjectionToken) languageStateService: ILanguageStateService,
    private readonly _standingState: PaymentStandingStateService,
    private readonly _router: Router
  ) {
    this.standing = this._standingState.standing;
    this.isAdmin = toSignal( roleAtLeast$( this._sessionStateService, "ADMIN" ), { initialValue: false } );
    this.language = toSignal( languageStateService.language$, { initialValue: "de" } );
    this.routerPath$ = this._router.events.pipe(filter(event => event instanceof NavigationEnd)).pipe(map((event) => <NavigationEnd>event)).pipe(map((event) => event.url));
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this._subscriptions.add(this._sessionStateService.activeReceiver$.subscribe((receiver: IReceiver | null) => {
      this._storeStateService.setActiveReceiverId(receiver?.id ?? null);
    }));

    this._subscriptions.add(this._sessionStateService.activeRole$.subscribe(role => {
      this._storeStateService.setCompanyRole(role);
    }));
    this._subscriptions.add(this._sessionStateService.activeCloud$.subscribe((cloud: ICloud | null) => {
      this._storeStateService.setActiveCloudId(cloud?.id ?? null);
    }));

    this._subscriptions.add(this._sessionStateService.activeCompany$.subscribe((company: ICompany | null) => {
      this._companyId = company?.id ?? null;
      this._loadStanding();
    }));
    this._subscriptions.add(this._standingState.reload$.subscribe(() => this._loadStanding()));

    this._twoFactorRestClient.getStatus().then( ( status ) => {
      this.mfaEnabled.set( status.enabled );
    } ).catch( () => {
      // Leave mfaEnabled at its initial true value so the banner stays hidden if status can't be read.
    } );
  }

  /** "Add payment method": the Stripe Customer Portal; Stripe sends the admin back to the payment settings. */
  public async openPaymentPortal(): Promise<void> {
    if ( !this._companyId ) return;
    this.openingPortal.set( true );
    try {
      const session = await this._billingRestClient.createPortalSession( this._companyId, this.language() === "en" ? "en" : "de" );
      window.location.href = session.url;
    } catch {
      this.openingPortal.set( false );
    }
  }

  private _loadStanding(): void {
    const companyId: string | null = this._companyId;
    if ( !companyId ) {
      this._standingState.standing.set( null );
      return;
    }
    this._billingRestClient.getStanding( companyId )
      .then( ( standing ) => {
        if ( companyId === this._companyId ) this._standingState.standing.set( standing );
      } )
      .catch( () => this._standingState.standing.set( null ) );
  }

  public goToMfaSettings(): void {
    const language: string = this._router.url.split( "/" )[ 1 ] || "en";
    this._router.navigate( [ `/${ language }/user-settings` ] );
  }
}
